<template>
  <div class="integral-gift-detail-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('integralGiftDetailManage.list.title')"
      :subtitle="$t('integralGiftDetailManage.page.subtitle')" />

    <!-- (2) SearchPanel -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.detailId" clearable
            :placeholder="$t('integralGiftDetailManage.search.detailId')" @keyup.enter.native="handleSearch" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.userNameLike" clearable
            :placeholder="$t('integralGiftDetailManage.search.userNameLike')" @keyup.enter.native="handleSearch" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.tel" clearable
            :placeholder="$t('integralGiftDetailManage.search.tel')" @keyup.enter.native="handleSearch" />
        </el-col>
      </el-row>

      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="handleSearch">
          {{ $t('common.search') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="handleReset">
          {{ $t('common.reset') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="page.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="getList">
        <template #left>
          <span class="ds-panel__title">{{ $t('integralGiftDetailManage.list.title') }}</span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="tableData" :loading="loading" :error="hasError" :error-message="errorMessage"
        :density="density" border :empty-description="$t('integralGiftDetailManage.page.empty')" @retry="getList">
        <el-table-column prop="detailId" :label="$t('integralGiftDetailManage.table.detailId')" align="center" width="140">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.detailId || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="acctName" :label="$t('integralGiftDetailManage.table.acctName')" align="center" min-width="140" show-overflow-tooltip />
        <el-table-column prop="configName" :label="$t('integralGiftDetailManage.table.configName')" align="left" min-width="150" show-overflow-tooltip />
        <el-table-column prop="ruleName" :label="$t('integralGiftDetailManage.table.ruleName')" align="center" min-width="140" show-overflow-tooltip />
        <el-table-column prop="quantity" :label="$t('integralGiftDetailManage.table.quantity')" align="right" width="100">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.quantity }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="userName" :label="$t('integralGiftDetailManage.table.userName')" align="center" width="120" />
        <el-table-column prop="tel" :label="$t('integralGiftDetailManage.table.tel')" align="center" width="140">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.tel || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="createTime" :label="$t('integralGiftDetailManage.table.createTime')" align="center" width="170">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.createTime || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('common.operation')" align="center" width="110" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="text" @click="handleDetail(scope.row)">
              {{ $t('common.detail') }}
            </el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="page.total" :page="page.current" :size="page.size" :page-sizes="[10, 20, 30, 50]"
        @size-change="handleSizeChange" @current-change="handleCurrentChange" />
    </div>
  </div>
</template>

<script>
import { listIntegralGiftDetail } from '@/api/scm/integralGiftDetailManageApi'
import { getCommunityId } from '@/api/community/communityApi'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'IntegralGiftDetailManageList',
  components: {
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      searchForm: {
        detailId: '',
        userNameLike: '',
        tel: '',
        communityId: ''
      },
      tableData: [],
      page: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.searchForm.communityId = getCommunityId()
    this.getList()
  },
  methods: {
    async getList() {
      try {
        this.loading = true
        this.hasError = false
        this.errorMessage = ''
        const params = {
          page: this.page.current,
          row: this.page.size,
          ...this.searchForm
        }
        const { data, total } = await listIntegralGiftDetail(params)
        this.tableData = Array.isArray(data) ? data : []
        this.page.total = total || 0
      } catch (error) {
        console.error('查询积分发放明细失败:', error)
        this.hasError = true
        this.errorMessage = error.message || this.$t('integralGiftDetailManage.page.loadFailed')
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.page.current = 1
      this.getList()
    },
    handleReset() {
      this.searchForm.detailId = ''
      this.searchForm.userNameLike = ''
      this.searchForm.tel = ''
      this.page.current = 1
      this.getList()
    },
    handleSizeChange(val) {
      this.page.size = val
      this.page.current = 1
      this.getList()
    },
    handleCurrentChange(val) {
      this.page.current = val
      this.getList()
    },
    handleDetail(row) {
      // 查看详情逻辑
      console.log(row)
    }
  }
}
</script>

<style lang="scss" scoped>
.integral-gift-detail-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}
</style>
